import React from 'react'
import { Div, Flex, Spacer, Text } from '@sendoutcards/quantum-design-ui'
import { Icon } from 'src/design_system/atoms/icons/components/Icon'
import { useSelector, useState } from 'src/hooks'
import { GiftCardBadge } from './components/GiftCardBadge'
import { GiftCardTile, GiftCardTileProps } from './components/GiftCardTile'
import { DigitalCardRecipientGiftCardRunaFragment } from 'src/graphql/generated/graphql'
import { useDigitalGiftCardProducts } from 'src/react_query'

type ReicipientType = {
  firstname: string
  lastname: string
  imageUrl?: string
}

type ActionType = {
  title?: string
  onClick: () => void
}

export type RecipientViewProps = {
  title?: string
  sender: ReicipientType
  giftCards: DigitalCardRecipientGiftCardRunaFragment[]
  primaryAction?: ActionType
  secondaryAction?: ActionType
  redeemGiftAction?: ActionType
}

export const RecipientView = (props: RecipientViewProps) => {
  const {
    title,
    sender,
    giftCards,
    primaryAction,
    secondaryAction,
    redeemGiftAction,
  } = props
  const fontFamily = 'Montserrat, sans-serif'

  const productQueries = useDigitalGiftCardProducts(
    giftCards.map(giftCard => giftCard.productCodes[0]),
  )
  const windowWidth = useSelector(state => state.window.width)
  const deviceBreakpoints = {
    mobile: 906,
    tablet: 710,
    phone: 512,
  }

  const isPhone = windowWidth <= deviceBreakpoints.phone
  const isTablet = windowWidth <= deviceBreakpoints.tablet
  const isMobileDevice = windowWidth <= deviceBreakpoints.mobile

  const senderStyles = sender.imageUrl
    ? {
        aspectRatio: '1/1',
        width: isMobileDevice ? '28px' : '44px',
        height: isMobileDevice ? '28px' : '44px',
        backgroundImage: `url(${sender.imageUrl})`,
        backgroundPostition: 'center',
        backgroundSize: 'contain',
        backgrounRepeat: 'no-repeat',
        borderRadius: '50px',
      }
    : {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        borderRadius: '50px',
        color: '#fff',
        backgroundColor: '#7C7C7C',
        boxShadow: '0px 0px 16px 0px rgba(0, 0, 0, 0.06)',
        fontSize: isMobileDevice ? '12px' : '20px',
        width: isMobileDevice ? '28px' : '44px',
        height: isMobileDevice ? '28px' : '44px',
      }

  const actionSharedStyles = {
    display: 'flex',
    cursor: 'pointer',
    borderRadius: '16px',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '8px 12px',
    flexDirection: 'row' as const,
    columnGap: '6px',
    fontFamily: 'Montserrat, sans-serif',
  }

  const recipientInitals = `${sender.firstname[0] + sender.lastname[0]}`

  const [isExpanded, setIsExpanded] = useState(false)
  const maxLength = (() => {
    if (isPhone) return 25
    if (isTablet) return 45
    if (isMobileDevice) return 70
    return 101
  })()

  const toggleExpanded = () => setIsExpanded(prev => !prev)

  const shouldTruncate = isMobileDevice && title && title.length > maxLength
  const displayText =
    shouldTruncate && !isExpanded ? `${title.slice(0, maxLength)}...` : title

  const giftCardsDetails: GiftCardTileProps[] = giftCards.map((giftCard, i) => {
    const product = productQueries[i]?.data
    return {
      currency: giftCard.currency,
      amount: giftCard.amount,
      giftCardPreviewUrl: product?.image ?? undefined,
      brand: product && {
        title: product.title,
        brandImage: product.image ?? undefined,
      },
    }
  })

  return (
    <Flex flexDirection="column" rowGap="x2" width="100%">
      <Flex justifyContent="space-between" alignItems="center">
        <Flex flexDirection="column" alignItems="flex-start">
          <Text
            type="body"
            style={{
              color: '#404040',
              fontSize: '16px',
              fontWeight: 700,
              fontFamily: fontFamily,
            }}
          >
            {displayText}
            {shouldTruncate && (
              <span
                onClick={toggleExpanded}
                style={{
                  color: '#727272c4',
                  fontSize: '10px',
                  fontWeight: 500,
                  cursor: 'pointer',
                  marginTop: '4px',
                  fontFamily: fontFamily,
                  whiteSpace: 'nowrap',
                }}
              >
                {isExpanded ? '(Show Less)' : 'View More'}
              </span>
            )}
          </Text>
          <Flex
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              columnGap: '7px',
            }}
          >
            <Div style={{ ...senderStyles }}>
              {!sender.imageUrl && recipientInitals}
            </Div>
            <Flex style={{ flexDirection: 'column' }}>
              <Text
                type="body"
                content="FROM"
                style={{
                  color: '#B7B7B7',
                  fontSize: '8px',
                  fontWeight: 700,
                  fontFamily: fontFamily,
                  lineHeight: 1.2,
                }}
              />
              <Text
                type="body"
                content={`${sender.firstname} ${sender.lastname}`}
                style={{
                  color: '#6B7280',
                  fontSize: '12px',
                  fontWeight: 500,
                  fontFamily: fontFamily,
                  lineHeight: 1.2,
                }}
              />
            </Flex>
          </Flex>
        </Flex>
        {isMobileDevice &&
          giftCardsDetails.map((details, i) => (
            <GiftCardBadge key={i} {...details} />
          ))}
      </Flex>
      <Flex
        flexDirection="column"
        columnGap="x1"
        width="100%"
        alignItems="center"
      >
        {!isMobileDevice &&
          giftCardsDetails.map((details, i) => (
            <React.Fragment key={i}>
              <GiftCardTile key={i} {...details} />
              <Spacer orientation="vertical" space="x2" />
            </React.Fragment>
          ))}
        <Flex
          flexDirection={isMobileDevice ? 'row' : 'column'}
          width="100%"
          style={{ gap: '1rem' }}
        >
          <Div
            onClick={primaryAction?.onClick}
            style={{
              ...actionSharedStyles,
              color: '#fff',
              boxShadow: '0px 0px 16px 0px rgba(0, 0, 0, 0.06)',
              backgroundColor: '#444',
              width: '100%',
              fontSize: '16px',
            }}
          >
            <Icon size={18} name="stars" />
            {primaryAction?.title}
          </Div>
          {giftCards.length > 0 && (
            <Div
              onClick={redeemGiftAction?.onClick}
              style={{
                ...actionSharedStyles,
                boxShadow: '0px 0px 8px 0px rgba(0, 0, 0, 0.04)',
                width: '100%',
                fontSize: '16px',
                backgroundColor: '#9959eb',
                color: '#fff',
              }}
            >
              {redeemGiftAction?.title}
            </Div>
          )}
        </Flex>
        <Flex
          onClick={secondaryAction?.onClick}
          cursor="pointer"
          justifyContent="center"
          alignItems="center"
          inset={{ top: '10px', bottom: '8px' }}
        >
          <Icon name="print_request" size={16} color="#A168E9" />
          <Text
            type="caption"
            content="Request a Printed Copy"
            outset={{ left: 'x1' }}
            style={{ color: '#A168E9', textDecoration: 'underline' }}
          />
        </Flex>
      </Flex>
    </Flex>
  )
}
